/**
 * Payment-history narrowing for AR/AP invoice documents.
 *
 * `filterPaymentTransactions` moved here VERBATIM from
 * `apps/web/src/lib/invoiceUtils.ts` (which is now a re-export shim) so the
 * server-side document assembler and the screen run the exact same filter —
 * this one is customer-facing, and a stale posting listed as a payment is
 * worse on a PDF than anywhere else in the app. Its sibling `voidedPayments`
 * deliberately stayed in web: it depends on `transactionTouchesBank`
 * (bank-account/account-category knowledge) and drives a web-only audit
 * section, not the document.
 */
/**
 * Filter an AR/AP invoice's `payment_transactions` array down to the journals
 * that are actually payments.
 *
 * Why: the PostgREST embed `payment_transactions:ledger_transactions!ar_invoice_id(...)`
 * (and the AP equivalent) returns every ledger transaction that has the
 * invoice's id in its `ar_invoice_id` / `ap_invoice_id` column. Three kinds of
 * journal land in that array that are not payments:
 *
 *  1. The invoice's own current posting (debit AR / credit Revenue for AR;
 *     credit AP / debit Expense for AP) — the transaction referenced by
 *     `accounts_receivable.ledger_transaction_id` /
 *     `accounts_payable.ledger_transaction_id`.
 *  2. A VOID journal. `void_ap_invoice` tags its reversal with the invoice id
 *     so the delete sweep can find every void, and `cancel_ap_invoice` always
 *     did. These carry `void_of_transaction_id`.
 *  3. A *superseded* posting. Force-editing an approved bill voids its posting
 *     and re-approval writes a new one, but the old journal keeps its
 *     `ap_invoice_id` — so a bill that has been edited once shows its previous
 *     posting here forever. It is identified by some other row in the array
 *     pointing at it via `void_of_transaction_id`.
 *
 * Without this, the bill's Payment History section renders those as
 * "Payment #1" alongside real payments. The DB-side equivalent of this filter
 * lives in `recompute_ap_invoice_paid` / `recompute_ar_invoice_received`; the
 * two must agree, or the header total and the listed payments disagree.
 *
 * They agree only because **every VOID journal carries its invoice's id**, so
 * it appears in the embed and case 3 can be detected from within the array.
 * The SQL side has no such constraint — its `NOT EXISTS` scans the whole table.
 * Migration `20260721123431` establishes the invariant (both `void_ap_invoice`
 * and `cancel_ap_invoice` stamp `ap_invoice_id`, and historical voids were
 * back-filled). A future void-minting path that omits the tag would silently
 * break this filter while leaving the DB totals correct.
 */
export function filterPaymentTransactions(transactions, creatingTransactionId) {
    if (!transactions)
        return [];
    // Journals reversed by a void that is itself in this array (case 3).
    const voidedIds = new Set(transactions
        .map((t) => t.void_of_transaction_id)
        .filter((id) => !!id));
    return transactions.filter((t) => t.id !== creatingTransactionId &&
        !t.void_of_transaction_id &&
        !voidedIds.has(t.id));
}
//# sourceMappingURL=payments.js.map